// store.ts — typed front-end wrappers over the on-device Rust SQLite commands.
// Every call hits LOCAL SQLite via Tauri IPC (durable, offline, on-disk). The climb
// tick list never leaves the device; only the DERIVED-SIGNALS object (see brain.ts /
// signals.rs) is sent for the once-per-season recap.
//
// Data model (matches src-tauri/src/db.rs::Climb):
//   climb { date, grade, environment, venue, discipline, style[], ascent, attempts,
//           sent, project, shoe, partner, note }

import { invoke } from "./tauri-web";

export type Environment = "indoor" | "outdoor";
export type Discipline = "boulder" | "sport" | "trad" | "toprope" | "other";
export type Ascent = "flash" | "onsight" | "redpoint" | "repeat" | "attempt";

export interface Climb {
  id: string;
  /** Calendar date the climb happened (local tz), "YYYY-MM-DD". */
  climbed_on: string;
  /** Raw grade string the climber typed ("V4", "5.11a", "6c+"). */
  grade: string;
  /** Internal ordinal for math (higher = harder); -1 if unparseable. Never shown raw. */
  grade_value: number;
  environment: Environment;
  /** Gym or crag name. */
  venue: string;
  discipline: Discipline;
  /** Style tags (overhang/slab/crimp/…), normalized lowercase. */
  style: string[];
  ascent: Ascent;
  /** Sessions/tries on this climb; 1 for a flash/onsight. */
  attempts: number;
  /** Topped out / clipped chains (true) vs an attempt only (false). */
  sent: boolean;
  /** An ongoing project line (groups across sessions in the recap). */
  project: boolean;
  shoe: string;
  partner: string;
  note: string;
  created_at: string;
  updated_at: string;
  /** Downscaled JPEG data-URL of a photo attached to this climb (e.g. the route or the send),
   *  ON-DEVICE only. "" when unset. The model never sees it — a photo filed through chat is an attachment. */
  photo: string;
}

export interface NewClimb {
  grade: string;
  climbed_on?: string;
  environment?: Environment;
  venue?: string;
  discipline?: Discipline;
  style?: string[];
  ascent?: Ascent;
  attempts?: number;
  sent?: boolean;
  project?: boolean;
  shoe?: string;
  partner?: string;
  note?: string;
}

export type ClimbPatch = Partial<{
  grade: string;
  climbed_on: string;
  environment: Environment;
  venue: string;
  discipline: Discipline;
  style: string[];
  ascent: Ascent;
  attempts: number;
  sent: boolean;
  project: boolean;
  shoe: string;
  partner: string;
  note: string;
}>;

// ── Derived signals (computed on-device in Rust; only aggregates are sent to the brain) ──
// Mirrors src-tauri/src/signals.rs::SeasonSignals. Every field is a computed aggregate —
// counts, splits, medians, drift — never a raw note. This is the payload the AI recap
// reasons over (see brain.ts), so the model can name SPECIFIC non-obvious patterns from
// the climber's logged season instead of re-reading the tick list.
export interface CountStat { key: string; count: number }

export interface Overall {
  total_climbs: number;
  total_sends: number;
  total_attempts_only: number;
  distinct_days: number;
  days_span: number;
  first_date: string | null;
  last_date: string | null;
  note: string;
}
export interface SeasonWindow {
  label: string;
  start: string;
  end: string;
  climbs: number;
  sends: number;
  attempts_only: number;
  distinct_days: number;
  distinct_venues: number;
}
export interface HardestSend {
  grade: string;
  climbed_on: string;
  venue: string;
  discipline: string;
  environment: string;
  ascent: string;
  ties_at_or_above: number;
}
export interface AttemptsBeforeTick {
  worked_sends: number;
  flashed_or_onsight: number;
  mean_attempts_worked: number | null;
  max_attempts: number | null;
  note: string;
}
export interface GradeDrift {
  first_half_median_value: number;
  first_half_label: string;
  first_half_sends: number;
  second_half_median_value: number;
  second_half_label: string;
  second_half_sends: number;
  delta_value: number;
  direction: "up" | "down" | "flat";
}
export interface EnvironmentSplit {
  indoor: number;
  outdoor: number;
  indoor_fraction: number | null;
}
export interface ProjectStat {
  grade: string;
  venue: string;
  discipline: string;
  total_attempts: number;
  sessions: number;
  sent: boolean;
  last_touched: string;
}
export interface SeasonSignals {
  schema: string;
  generated_for: string;
  overall: Overall;
  season: SeasonWindow;
  hardest: HardestSend | null;
  attempts_before_tick: AttemptsBeforeTick;
  grade_drift: GradeDrift | null;
  environment_split: EnvironmentSplit;
  ascent_mix: CountStat[];
  discipline_mix: CountStat[];
  style_mix: CountStat[];
  top_venues: CountStat[];
  top_partners: CountStat[];
  projects: ProjectStat[];
}

export const store = {
  listClimbs: (opts?: { from?: string; to?: string; limit?: number }) =>
    invoke<Climb[]>("list_climbs", { from: opts?.from, to: opts?.to, limit: opts?.limit ?? 50000 }),
  getClimb: (id: string) => invoke<Climb | null>("get_climb", { id }),
  createClimb: (input: NewClimb) => invoke<Climb>("create_climb", { input }),
  updateClimb: (id: string, patch: ClimbPatch) => invoke<void>("update_climb", { id, patch }),
  /** Attach a photo (downscaled JPEG data-URL) to a climb — filed ON-DEVICE from chat (e.g. the
   *  route or the send). Never leaves the device, never sent to the model. "" clears it. */
  setClimbPhoto: (id: string, photo: string) => invoke<void>("set_climb_photo", { id, photo }),
  deleteClimb: (id: string) => invoke<void>("delete_climb", { id }),
  /** Compute the derived-signals object on-device. `today` = client local YYYY-MM-DD so the season window aligns to the climber's clock. */
  computeSignals: (today: string) => invoke<SeasonSignals>("compute_signals", { today }),
};

// ── Pure policy / presentation helpers ─────────────────────────────────────────

export const DISCIPLINE_LABELS: Record<Discipline, string> = {
  boulder: "Boulder",
  sport: "Sport",
  trad: "Trad",
  toprope: "Top-rope",
  other: "Other",
};

export const ASCENT_LABELS: Record<Ascent, string> = {
  flash: "Flash",
  onsight: "Onsight",
  redpoint: "Redpoint",
  repeat: "Repeat",
  attempt: "Attempt",
};

/** Short human label + whether this ascent counts as a send. */
export function isSendAscent(a: Ascent): boolean {
  return a !== "attempt";
}

export const ENVIRONMENT_LABELS: Record<Environment, string> = {
  indoor: "Indoor",
  outdoor: "Outdoor",
};

/** Curated style-tag suggestions; the climber can also type their own. */
export const DEFAULT_STYLES = [
  "overhang", "slab", "vertical", "crimp", "sloper", "jug", "pinch",
  "dyno", "mantle", "compression", "technical", "powerful", "endurance", "balance",
];

/** Common ascent options for the picker, in a natural order. */
export const ASCENTS: Ascent[] = ["flash", "onsight", "redpoint", "repeat", "attempt"];
export const DISCIPLINES: Discipline[] = ["boulder", "sport", "trad", "toprope", "other"];
export const ENVIRONMENTS: Environment[] = ["indoor", "outdoor"];

/** Normalize a style tag — lowercase, trimmed, whitespace→hyphen. */
export function normalizeTag(raw: string): string {
  return raw.trim().toLowerCase().replace(/\s+/g, "-");
}
/** Dedupe + normalize, preserving first-occurrence order. */
export function normalizeTags(raw: Iterable<string>): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const r of raw) {
    const n = normalizeTag(r);
    if (!n || seen.has(n)) continue;
    seen.add(n);
    out.push(n);
  }
  return out;
}

const toLocalDate = (iso: string): Date | null => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return null;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
};
export const formatLocalDate = (d: Date): string =>
  `${d.getFullYear().toString().padStart(4, "0")}-${(d.getMonth() + 1).toString().padStart(2, "0")}-${d.getDate().toString().padStart(2, "0")}`;
export const todayLocal = (): string => formatLocalDate(new Date());

// ── At-a-glance corpus stats for the catalog screen (pure, on-device, no AI) ───

export interface CorpusStats {
  total: number;
  sends: number;
  attemptsOnly: number;
  distinctDays: number;
  thisYearSends: number;
  /** The raw grade string of the hardest send overall (by ordinal); null if none parseable. */
  hardestGrade: string | null;
}

/** Quick rollups for the home hero — purely descriptive, computed locally. */
export function corpusStats(climbs: Climb[], today = new Date()): CorpusStats {
  let sends = 0;
  const days = new Set<string>();
  const yearCutoff = formatLocalDate(new Date(today.getFullYear(), 0, 1));
  let thisYearSends = 0;
  let hardestVal = -1;
  let hardestGrade: string | null = null;
  for (const c of climbs) {
    const d = toLocalDate(c.climbed_on);
    if (d) days.add(formatLocalDate(d));
    if (c.sent) {
      sends++;
      if (c.climbed_on >= yearCutoff) thisYearSends++;
      if (c.grade_value > hardestVal) {
        hardestVal = c.grade_value;
        hardestGrade = c.grade || null;
      }
    }
  }
  return {
    total: climbs.length,
    sends,
    attemptsOnly: climbs.length - sends,
    distinctDays: days.size,
    thisYearSends,
    hardestGrade,
  };
}

/**
 * Climbing-day streak = consecutive run of days (from today, walking back) that have
 * at least one logged climb, with one day of grace (logging this morning doesn't break
 * yesterday's streak). Climbers log per session, not daily — so this counts session days.
 */
export function computeStreak(loggedOnDates: string[], today = new Date()): number {
  if (loggedOnDates.length === 0) return 0;
  const cursor = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const days = new Set<string>();
  for (const d of loggedOnDates) {
    const parsed = toLocalDate(d);
    if (parsed) days.add(formatLocalDate(parsed));
  }
  let cur = new Date(cursor);
  if (!days.has(formatLocalDate(cur))) {
    cur.setDate(cur.getDate() - 1);
    if (!days.has(formatLocalDate(cur))) return 0;
  }
  let streak = 0;
  while (days.has(formatLocalDate(cur))) {
    streak++;
    cur.setDate(cur.getDate() - 1);
  }
  return streak;
}

export function streakCopy(streak: number): string {
  if (streak === 0) return "Log a session to start one";
  if (streak === 1) return "1 day on — first tick of the run";
  if (streak < 4) return `${streak} climbing days in a row`;
  if (streak < 8) return `${streak} days on · solid block`;
  return `${streak} days on · big block of sessions`;
}
